import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Props = {
  value: string;
  onChange: (value: string) => void;
  availableDates: string[];
  label: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
  minDate?: string;
  maxDate?: string;
  className?: string;
  icon?: ReactNode;
  variant?: "plain" | "field";
  restrictToAvailableDates?: boolean;
};

const MONTH_LABELS = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];
const WEEKDAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function toDate(value: string) {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDate(value: string) {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}.${month}.${year}`;
}

function getInitialMonth(value: string, availableDates: string[]) {
  const selectedDate = toDate(value);
  if (selectedDate) return selectedDate;
  const latestAvailableDate = availableDates[availableDates.length - 1];
  return toDate(latestAvailableDate) ?? new Date();
}

function buildMonthDays(monthDate: Date) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const firstWeekday = (firstDay.getDay() + 6) % 7;
  const startDate = new Date(year, month, 1 - firstWeekday);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + index);
    return date;
  });
}

export default function AvailableDatePicker({
  value,
  onChange,
  availableDates,
  label,
  disabled = false,
  loading = false,
  error = null,
  minDate,
  maxDate,
  className = "",
  icon,
  variant = "plain",
  restrictToAvailableDates = true,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ left: 0, top: 0 });
  const [monthDate, setMonthDate] = useState(() => getInitialMonth(value, availableDates));
  const availableSet = useMemo(() => new Set(availableDates), [availableDates]);
  const minTime = minDate ? toDate(minDate)?.getTime() ?? null : null;
  const maxTime = maxDate ? toDate(maxDate)?.getTime() ?? null : null;
  const isDisabled = disabled || loading || Boolean(error);
  const monthDays = useMemo(() => buildMonthDays(monthDate), [monthDate]);
  const isFieldVariant = variant === "field";
  const updatePopoverPosition = useCallback(() => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const popoverWidth = 304;
    const viewportWidth = window.innerWidth;
    const left = Math.min(Math.max(rect.left, 8), Math.max(8, viewportWidth - popoverWidth - 8));
    setPopoverPosition({ left, top: rect.bottom + 8 });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePopoverPosition();
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!wrapperRef.current?.contains(target) && !popoverRef.current?.contains(target)) setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", updatePopoverPosition, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", updatePopoverPosition, true);
    };
  }, [open, updatePopoverPosition]);

  useEffect(() => {
    if (!value) return;
    const nextMonth = toDate(value);
    if (nextMonth) setMonthDate(nextMonth);
  }, [value]);

  const moveMonth = (offset: number) => {
    setMonthDate((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  const isDateEnabled = (isoDate: string) => {
    if (restrictToAvailableDates && !availableSet.has(isoDate)) return false;
    const dateTime = toDate(isoDate)?.getTime() ?? null;
    if (dateTime === null) return false;
    if (minTime !== null && dateTime < minTime) return false;
    if (maxTime !== null && dateTime > maxTime) return false;
    return true;
  };

  return (
    <div ref={wrapperRef} className={`relative min-w-0 ${className}`}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => {
          updatePopoverPosition();
          setOpen((current) => !current);
        }}
        className={[
          "min-w-0 w-full text-left text-[15px] text-slate-800 transition",
          isFieldVariant
            ? "flex min-h-[44px] items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm"
            : "rounded-xl bg-transparent px-1 py-2",
          "focus:outline-none focus:ring-4 focus:ring-indigo-200/60",
          isDisabled ? "cursor-not-allowed opacity-60" : "hover:border-slate-300 hover:text-indigo-700",
        ].join(" ")}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {icon && <span className="shrink-0 text-slate-400">{icon}</span>}
        <span className="min-w-0 truncate">{loading ? "Загрузка..." : value ? formatDate(value) : "Не выбрано"}</span>
      </button>

      {open && !isDisabled && createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[9999] w-[304px] rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_20px_45px_rgba(15,23,42,0.18)]"
          style={{ left: popoverPosition.left, top: popoverPosition.top }}
        >
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => moveMonth(-1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100"
              aria-label="Предыдущий месяц"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="text-sm font-semibold text-slate-800">
              {MONTH_LABELS[monthDate.getMonth()]} {monthDate.getFullYear()}
            </div>
            <button
              type="button"
              onClick={() => moveMonth(1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100"
              aria-label="Следующий месяц"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase text-slate-400">
            {WEEKDAY_LABELS.map((weekday) => (
              <div key={weekday} className="py-1">
                {weekday}
              </div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {monthDays.map((date) => {
              const isoDate = toIsoDate(date);
              const isCurrentMonth = date.getMonth() === monthDate.getMonth();
              const isSelected = value === isoDate;
              const enabled = isDateEnabled(isoDate);
              return (
                <button
                  key={isoDate}
                  type="button"
                  disabled={!enabled}
                  onClick={() => {
                    if (!enabled) return;
                    onChange(isoDate);
                    setOpen(false);
                  }}
                  className={[
                    "h-9 rounded-xl text-sm font-semibold transition",
                    isSelected ? "bg-indigo-600 text-white shadow-sm" : "",
                    !isSelected && enabled ? "text-slate-700 hover:bg-indigo-50 hover:text-indigo-700" : "",
                    !enabled ? "cursor-not-allowed text-slate-300 line-through opacity-70" : "",
                    !isCurrentMonth && enabled ? "text-slate-400" : "",
                  ].join(" ")}
                  aria-label={formatDate(isoDate)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
